import React, { Fragment } from 'react';

import styles from './UserMenu.module.scss';
import colors from '../colors.json';
import { AdditionalLoginLinkShape } from './Login';
import { BodyText, Div, Divider, Flex, Gutter, Icon, IconName, Link, Title } from '@gaiads/telia-react-component-library';

type UserMenuProps = {
  /** User's name */
  username: string;
  userMenuLinks?: UserMenuLink[];
  logoutButton: {
    /** Logout button label */
    logoutButtonLabel: string;
    /** Logout button onClick callback */
    onLogoutButtonClick: () => void;
  };
  /** Additional links on the bottom of the menu */
  additionalLinks?: AdditionalLoginLinkShape[];
};

export type UserMenuLink = {
  /** Name of the service user is logged into */
  label: string;
  /** Name of the icon */
  icon?: IconName;
  /** Link to service user is logged in */
  link: string;
  /** Unique key */
  key?: string;
  /** Open in new window */
  openInNewWindow?: boolean;
};

const UserMenu: React.FunctionComponent<UserMenuProps> = ({ username, userMenuLinks, logoutButton, additionalLinks }: UserMenuProps) => {
  return (
    <Div className={styles.userMenu} data-testid="logged-in">
      <Title color="black" block={true} className={styles.userMenu_username} size="xxs" data-testid="logged-in-username">
        {username}
      </Title>

      {userMenuLinks && (
        <>
          <Gutter size="sm" />

          {userMenuLinks.map(({ link, label, key, icon, openInNewWindow }) => (
            <Fragment key={key ? key : label}>
              <Link block={true} className={styles.userMenu_link} data-testid="logged-in-link" to={link} openInNewWindow={openInNewWindow}>
                <Flex centeredVertically>
                  {icon && (
                    <>
                      <Icon name={icon} color="inherit" size="sm" />
                      <Gutter size="xs" />
                    </>
                  )}

                  <BodyText tagName="span" size="sm">
                    {label}
                  </BodyText>
                </Flex>
              </Link>

              <Gutter size="sm" />
            </Fragment>
          ))}
        </>
      )}

      <Divider color="gray300" width={80} />

      <Flex
        backgroundHoverColor="gray200"
        className={styles.userMenu_logoutButton}
        data-testid="logged-in-logout-button"
        onClick={logoutButton.onLogoutButtonClick}
        centeredVertically
      >
        <svg viewBox="0 0 64 64" height="18" width="18">
          <path
            fill={colors.black.color}
            d="M57.41278,33.41534,45.41406,45.41406a1.99978,1.99978,0,0,1-2.82812-2.82812L51.17151,34H24a2,2,0,0,1,0-4H51.17151l-8.58557-8.58594a1.99978,1.99978,0,0,1,2.82812-2.82812L57.41278,30.58466a2.00855,2.00855,0,0,1,.25073.30664c.03174.04748.05335.099.08057.14843a1.17162,1.17162,0,0,1,.165.39539c.01715.05786.03949.11334.05139.17334a2.00573,2.00573,0,0,1,0,.78308c-.0119.06-.03424.11548-.05139.17334a1.17162,1.17162,0,0,1-.165.39539c-.02722.04943-.04883.10095-.08057.14843A2.00855,2.00855,0,0,1,57.41278,33.41534ZM34,52H16a4.00427,4.00427,0,0,1-4-4V16a4.00427,4.00427,0,0,1,4-4H34a2,2,0,0,0,0-4H16a8.00917,8.00917,0,0,0-8,8V48a8.00917,8.00917,0,0,0,8,8H34a2,2,0,0,0,0-4Z"
          />
        </svg>

        <Gutter size="xs" />

        <BodyText tagName="span">{logoutButton.logoutButtonLabel}</BodyText>
      </Flex>

      {additionalLinks && (
        <>
          {additionalLinks.map(({ label, link, icon, key }) => {
            return (
              <Fragment key={key ? key : label}>
                <Divider color="gray300" />

                <Link color="gray800" hoverColor="gray800" to={link} data-testid="additional-link">
                  <Flex backgroundColor="gray100" backgroundHoverColor="gray300" className={styles.userMenu_additionalLinkContent} centeredVertically>
                    <BodyText tagName="span">{label}</BodyText>

                    {icon && (
                      <>
                        <Gutter size="xs" flexItem />

                        <Icon name={icon} size="sm" color="gray500" />
                      </>
                    )}
                  </Flex>
                </Link>
              </Fragment>
            );
          })}
        </>
      )}
    </Div>
  );
};

export default UserMenu;
